import "../Styles/ProjectSubmissions.css";
import React, { useEffect, useState } from "react";
import {
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Download,
  Eye,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const ProjectSubmissions = () => {
  const [user] = useState(JSON.parse(localStorage.getItem("student")));
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  const projectsPerPage = 8;

  // Fetch project submissions
  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ADMIN_BASE_URL}/api/get-submitted-projects`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ UID: user.student.UID }),
        }
      );

      const result = await response.json();
      setProjects(result?.data || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Filter and sort projects
  const filteredProjects = projects
    .filter((project) => {
      const matchesSearch =
        project.projectId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.title?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || project.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.createdAt) - new Date(a.createdAt);
        case "oldest":
          return new Date(a.createdAt) - new Date(b.createdAt);
        case "status":
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });

  // Pagination logic
  const indexOfLast = currentPage * projectsPerPage;
  const indexOfFirst = indexOfLast - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle size={20} />;
      case "rejected":
        return <XCircle size={20} />;
      default:
        return <Clock size={20} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "#10b981";
      case "rejected":
        return "#ef4444";
      default:
        return "#f59e0b";
    }
  };

  const handleRefresh = () => {
    fetchProjects();
  };

  return (
    <div className="ps-container">
      <div className="ps-header">
        <div className="ps-header-content">
          <h1 className="ps-heading">Project Submissions</h1>
          <p className="ps-subtitle">
            Track and manage your project submissions
          </p>
        </div>
        <button className="ps-refresh-btn" onClick={handleRefresh}>
          Refresh
        </button>
      </div>

      {/* Stats Overview */}
      <div className="ps-stats-grid">
        <div className="ps-stat-card">
          <div className="ps-stat-icon total">
            <CheckCircle size={24} />
          </div>
          <div className="ps-stat-content">
            <span className="ps-stat-number">{projects.length}</span>
            <span className="ps-stat-label">Total Submissions</span>
          </div>
        </div>
        <div className="ps-stat-card">
          <div className="ps-stat-icon approved">
            <CheckCircle size={24} />
          </div>
          <div className="ps-stat-content">
            <span className="ps-stat-number">
              {projects.filter((p) => p.status === "completed").length}
            </span>
            <span className="ps-stat-label">Completed</span>
          </div>
        </div>
        <div className="ps-stat-card">
          <div className="ps-stat-icon pending">
            <Clock size={24} />
          </div>
          <div className="ps-stat-content">
            <span className="ps-stat-number">
              {projects.filter((p) => p.status === "pending").length}
            </span>
            <span className="ps-stat-label">Pending</span>
          </div>
        </div>
        <div className="ps-stat-card">
          <div className="ps-stat-icon rejected">
            <XCircle size={24} />
          </div>
          <div className="ps-stat-content">
            <span className="ps-stat-number">
              {projects.filter((p) => p.status === "rejected").length}
            </span>
            <span className="ps-stat-label">Rejected</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="ps-controls">
        <div className="ps-search-container">
          <Search size={20} className="ps-search-icon" />
          <input
            type="text"
            placeholder="Search projects by ID or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ps-search-input"
          />
        </div>

        <div className="ps-filters">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="ps-filter-select"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="ps-filter-select"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="status">By Status</option>
          </select>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="ps-loading-container">
          <div className="ps-loading-spinner"></div>
          <p className="ps-loading-text">Loading your projects...</p>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="ps-empty-state">
          <AlertCircle size={48} className="ps-empty-icon" />
          <h3 className="ps-empty-title">No projects found</h3>
          <p className="ps-empty-description">
            {searchTerm || statusFilter !== "all"
              ? "Try adjusting your search or filters"
              : "You haven't submitted any projects yet"}
          </p>
        </div>
      ) : (
        <>
          <div className="ps-grid">
            {currentProjects.map((item) => (
              <div key={item._id} className="ps-card">
                <div className="ps-card-header">
                  <h3 className="ps-project-title">
                    {item.title || `Project ${item.projectId}`}
                  </h3>
                  <div
                    className="ps-status-badge"
                    style={{ backgroundColor: getStatusColor(item.status) }}
                  >
                    {getStatusIcon(item.status)}
                    <span>
                      {item.status.charAt(0).toUpperCase() +
                        item.status.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="ps-card-body">
                  <div className="ps-info-item">
                    <strong>Project ID:</strong>
                    <span className="ps-project-id">{item.projectId}</span>
                  </div>

                  <div className="ps-info-item">
                    <strong>Submitted:</strong>
                    <span className="ps-date">
                      {new Date(item.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  </div>

                  <div className="ps-info-item">
                    <strong>Time:</strong>
                    <span className="ps-time">
                      {new Date(item.createdAt).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>

                  {item.feedback && (
                    <div className="ps-feedback">
                      <strong>Instructor Feedback:</strong>
                      <p className="ps-feedback-text">{item.feedback}</p>
                    </div>
                  )}
                </div>

                {/* <div className="ps-card-actions">
                  <button className="ps-action-btn view">
                    <Eye size={16} />
                    View Details
                  </button>
                  <button className="ps-action-btn download">
                    <Download size={16} />
                    Download
                  </button>
                </div> */}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="ps-pagination">
              <button
                disabled={currentPage === 1}
                className="ps-pagination-btn"
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <ChevronLeft size={16} />
                Previous
              </button>

              <div className="ps-pagination-info">
                <span className="ps-page-number">
                  Page {currentPage} of {totalPages}
                </span>
                <span className="ps-results-count">
                  {filteredProjects.length} projects found
                </span>
              </div>

              <button
                disabled={currentPage === totalPages}
                className="ps-pagination-btn"
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProjectSubmissions;
